import { useCallback, useMemo, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';
import { usePermission } from '@/hooks/usePermission';
import { CLIENT_DETAIL_TABS, TAB_LABELS, TAB_PERMISSIONS, type ClientDetailTab } from './types';

interface ClientTabsProps {
  activeTab: ClientDetailTab;
  onTabChange: (tab: ClientDetailTab) => void;
}

export function ClientTabs({ activeTab, onTabChange }: ClientTabsProps) {
  const { hasAny } = usePermission();

  const visibleTabs = useMemo<readonly ClientDetailTab[]>(
    () => CLIENT_DETAIL_TABS.filter((tab) => hasAny(TAB_PERMISSIONS[tab])),
    [hasAny],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (visibleTabs.length === 0) return;
      const currentIndex = visibleTabs.indexOf(activeTab);
      // If the URL-selected tab is hidden (permission stripped after page load),
      // arrow keys still let the operator walk through the visible set.
      const baseIndex = currentIndex === -1 ? 0 : currentIndex;
      let newIndex: number | null = null;

      if (e.key === 'ArrowRight') {
        newIndex = (baseIndex + 1) % visibleTabs.length;
      } else if (e.key === 'ArrowLeft') {
        newIndex = (baseIndex - 1 + visibleTabs.length) % visibleTabs.length;
      } else if (e.key === 'Home') {
        newIndex = 0;
      } else if (e.key === 'End') {
        newIndex = visibleTabs.length - 1;
      }

      if (newIndex !== null) {
        e.preventDefault();
        const newTab = visibleTabs[newIndex];
        onTabChange(newTab);
        document.getElementById(`tab-${newTab}`)?.focus();
      }
    },
    [activeTab, onTabChange, visibleTabs],
  );

  return (
    <div
      className="mb-6 flex gap-0 border-b-2 border-[#e8e8e8]"
      role="tablist"
      aria-label="Client detail tabs"
      onKeyDown={handleKeyDown}
    >
      {visibleTabs.map((tab) => (
        <button
          key={tab}
          role="tab"
          aria-selected={activeTab === tab}
          aria-controls={`tabpanel-${tab}`}
          id={`tab-${tab}`}
          tabIndex={activeTab === tab ? 0 : -1}
          className={cn(
            'relative cursor-pointer border-none bg-transparent px-5 py-3 text-[13px] font-semibold transition-colors',
            activeTab === tab
              ? 'text-[#6B0F1A] after:absolute after:bottom-[-2px] after:left-0 after:right-0 after:h-[2px] after:bg-[#6B0F1A] after:content-[""]'
              : 'text-[#999] hover:text-[#666]',
          )}
          onClick={() => onTabChange(tab)}
        >
          {TAB_LABELS[tab]}
        </button>
      ))}
    </div>
  );
}
